
export enum Genre {
  FLYTE_TIME = 'flyte-time',
  CAPRICORN = 'capricorn',
  MUSCLE_SHOALS = 'muscle-shoals',
  STAX = 'stax',
  BAKERSFIELD = 'bakersfield',
  NASHVILLE = 'nashville',
  PHILLY = 'philly',
  MOTOWN = 'motown',
  AMERICANA = 'americana',
  COUNTRY_SALOON = 'country-saloon',
  DOOM_METAL = 'doom-metal',
  SOUTHERN_ROCK_PORCH = 'southern-rock-porch',
  SOUTHERN_METAL = 'southern-metal',
  WEST_COAST_HIP_HOP = 'west-coast-hip-hop',
  CHORAL_SLUDGE = 'choral-sludge',
  RIFF_GRAVEYARD = 'riff-graveyard',
  ALBUM_ORIENTED_ROCK = 'album-oriented-rock',
  CLASSICAL = 'classical',
}

export interface EraConfig {
  id: string;
  name: string;
  years: string;
  signature: string;
  references: string[];
  instruments: string[];
}

export interface VocalStyleConfig {
  id: string;
  name: string;
  artist: string;
  description: string;
  deliveryNotes: string;
  availableEras: string[]; // Era IDs
}

export interface SectorConfig {
  id: string;
  name: string;
  icon: string;
  description: string;
  bpmRange: [number, number];
  moodKeywords: string[];
}

export interface StructureConfig {
  id: string;
  name: string;
  pattern: string;
  bpmRange: [number, number];
  description: string;
}

export interface ModuleConfig {
  id: Genre;
  tabName: string;
  sectorLabel: string;
  tagline: string;
  eras: EraConfig[];
  vocalStyles: VocalStyleConfig[];
  sectors: SectorConfig[];
  structures: StructureConfig[];
  keyPools: {
    major: string[];
    minor: string[];
  };
}

export type RiffWriterView = 'discovery' | 'builder';

export interface LayerAttribution {
  rhythm: string;
  harmony: string;
  vocals: string;
  instrumentation: string;
  production: string;
}

export interface HybridConfig {
  /** Secondary genre slug — primary is the seed's main `genre` field. */
  secondary: Genre;
  /** % primary (50–90). Higher = primary dominates. */
  intensity: number;
  /** When true, secondary genre owns the rhythm section (Run-DMC pattern). */
  secondaryOwnsRhythm: boolean;
}

export interface HybridMeta extends HybridConfig {
  /** LLM-reported per-layer ownership + specific musical choice. */
  layerAttribution: LayerAttribution;
}

export interface SongSeed {
  id: string;
  timestamp: number;
  genre: Genre;
  title: string;
  concept: string;
  userTheme?: string;
  era: string;
  vocalStyle: string;
  povSetting: string;
  mustHaves: string[];
  avoid: string[];
  instruments: string[];
  structure: string;
  bpm: number;
  key: string;
  /** Present only when generated in hybrid mode. */
  hybrid?: HybridMeta;
}

export interface ComposerDescriptor {
  id: string;
  name: string;
  era: string;
  behavioralDescriptor: string;
  signatureSound: string;
  defaultInstrumentation: string[];
}

export interface ClassicalFormConfig {
  id: string;
  name: string;
  description: string;
  sections: string[];
  typicalEras: string[];
}

export interface InstrumentSection {
  section: string;
  instruments: string[];
}

export interface EnsemblePreset {
  id: string;
  name: string;
  instruments: string[];
}

export interface ClassicalExtensions {
  instrumental: boolean;
  selectedInstruments: string[];
  ensemblePreset: string | null;
  form: string | null;
  keyJourney: {
    startKey: string;
    intermediateKeys: string[];
    endKey: string;
    picardyThird: boolean;
  };
  tempoMarking: string | null;
  bpmOverride: number | null;
  motivicCell: string;
  performanceNotes: string;
  negativeStyle: string;
}
